// services/incidentsService.ts
import { apiClient } from '@/services/api-client'
import { fetchAllPointPages, sharedClientPointPages } from '@/services/pointCatalogue'
import type {
  Incident,
  CreateIncidentRequest,
  UpdateIncidentRequest,
  IncidentsResponse,
  Supervisor,
  Agency,
  ClaimOperationResponse,
  EligibleAssignee,
  IncidentClassificationOption,
} from '@/types/incidents'

export const incidentsService = {
  // 📋 Obtener lista de incidencias con filtros.
  // Fase 4 — Lote 4.0: sin bypass de rol, sin fallback a mocks, sin enrichment
  // aleatorio. El backend es la única fuente de verdad. Errores se propagan.
  async getIncidents(filters: any = {}): Promise<IncidentsResponse> {
    const params = new URLSearchParams()

    // Aplicar filtros.
    // P13-V7B (refutation round 3): only filters the list reader HONOURS are sent, in its vocabulary.
    // It compares `status` and `priority` by EQUALITY with the stored lowercase value, one value per
    // field (`status=PENDING,IN_PROGRESS` matched nothing and was published as "0 de 0"); it reads the
    // agency as `pointId` (`agency` was ignored); it has no category, supervisor or date filter.
    // Several values of one field cannot be asked of it: those are applied by the page to the rows it
    // shows, and the page says so.
    const STORED_STATUS: Record<string, string> = {
      PENDING: 'pending', IN_PROGRESS: 'in_progress', PENDING_VALIDATION: 'pending_validation',
      CLOSED: 'closed', CANCELLED: 'cancelled'
    }
    const STORED_PRIORITY = new Set(['low', 'medium', 'high', 'critical'])
    if (filters.search) params.append('search', filters.search)
    if (filters.status?.length === 1 && STORED_STATUS[String(filters.status[0]).toUpperCase()]) {
      params.append('status', STORED_STATUS[String(filters.status[0]).toUpperCase()])
    }
    if (filters.priority?.length === 1 && STORED_PRIORITY.has(String(filters.priority[0]).toLowerCase())) {
      params.append('priority', String(filters.priority[0]).toLowerCase())
    }
    if (filters.agency !== null && filters.agency !== undefined && filters.agency !== '') {
      params.append('pointId', String(filters.agency))
    }

    // Paginación y orden opcionales
    if (filters.page) params.append('page', String(filters.page))
    if (filters.limit) params.append('limit', String(filters.limit))
    if (filters.sortBy) params.append('sortBy', String(filters.sortBy))
    if (filters.sortDir) params.append('sortDir', String(filters.sortDir))

    // P13-V7B (refutation): the list is PAGINATED (default 50, max 200) and its total travels on the
    // envelope, beside `data`. Unwrapped, `count` was lost and the page length was published as the
    // total. The envelope is kept so the server's count is the total.
    const response = await apiClient.get(`/incidences?${params.toString()}`, { skipUnwrap: true } as any)

    // Contrato del backend: { success: true, data: [...], count, page, limit, totalPages }
    let incidents: any[]
    let total: number

    if (response.data?.success && Array.isArray(response.data.data)) {
      incidents = response.data.data
      total = Number(response.data.count ?? response.data.data.length)
    } else if (Array.isArray(response.data?.data)) {
      incidents = response.data.data
      total = Number(response.data.count ?? response.data.data.length)
    } else if (Array.isArray(response.data)) {
      incidents = response.data
      total = response.data.length
    } else {
      // Cualquier otro formato es contrato roto: error real, sin fallback simulado.
      throw new Error('Formato inesperado en /incidences: el backend no respeta el contrato { success, data, count }')
    }

    // Mapeo defensivo de los enums conocidos del backend a los del frontend.
    // No se inventan campos faltantes: lo que el backend no envía, no se rellena.
    const mapPriority = (backendPriority: string | null | undefined) => {
      if (!backendPriority) return null
      const priorityMap: Record<string, string> = {
        'ALTA': 'HIGH',
        'MEDIA': 'MEDIUM',
        'BAJA': 'LOW',
        'CRITICA': 'CRITICAL',
        'CRÍTICA': 'CRITICAL',
        'HIGH': 'HIGH',
        'MEDIUM': 'MEDIUM',
        'LOW': 'LOW',
        'CRITICAL': 'CRITICAL'
      }
      return priorityMap[backendPriority.toUpperCase()] || backendPriority
    }

    const mapStatus = (backendStatus: string | null | undefined) => {
      if (!backendStatus) return null
      const statusMap: Record<string, string> = {
        'ABIERTO': 'PENDING',
        'PENDIENTE': 'PENDING',
        'EN_PROCESO': 'IN_PROGRESS',
        'EN PROCESO': 'IN_PROGRESS',
        'RESUELTO': 'CLOSED',
        'CERRADO': 'CLOSED',
        'CANCELADO': 'CANCELLED',
        'PENDIENTE_VALIDACION': 'PENDING_VALIDATION',
        'PENDING': 'PENDING',
        'IN_PROGRESS': 'IN_PROGRESS',
        'RESOLVED': 'CLOSED',
        'PENDING_VALIDATION': 'PENDING_VALIDATION',
        'CLOSED': 'CLOSED',
        'CANCELLED': 'CANCELLED'
      }
      return statusMap[backendStatus.toUpperCase()] || backendStatus
    }

    const mappedData = incidents.map((incident: any) => ({
      ...incident,
      priority: mapPriority(incident.priority),
      status: mapStatus(incident.status)
    }))

    return {
      data: mappedData as any,
      total,
      page: Number(response.data?.page ?? filters.page ?? 1),
      limit: Number(response.data?.limit ?? filters.limit ?? 100)
    }
  },

  // 📄 Obtener detalles completos de una incidencia por ID
  async getIncidentById(id: number, options?: { cacheBuster?: number }): Promise<Incident> {
    try {
      // Agregar cacheBuster como query param para evitar cache HTTP del navegador
      const url = options?.cacheBuster 
        ? `/incidences/${id}?_=${options.cacheBuster}`
        : `/incidences/${id}`
      
      const response = await apiClient.get(url)
      
      // El backend devuelve el objeto directamente con statusHistory incluido
      const incident = response.data
      
      return {
        ...incident,
        // Normalizar a UPPERCASE para consistencia con el frontend
        priority: incident.priority?.toUpperCase() || 'MEDIUM',
        status: incident.status?.toUpperCase() || 'PENDING',
        category: incident.category?.toUpperCase() || 'OTHER',
        // Asegurar que statusHistory esté presente
        statusHistory: incident.statusHistory || []
      }
    } catch (error) {
      console.error('❌ Error obteniendo detalles de incidencia:', error)
      throw error
    }
  },

  // 📝 Crear nueva incidencia
  async createIncident(incident: CreateIncidentRequest): Promise<Incident> {
    try {
      const response = await apiClient.post('/incidences', incident)
      console.log('✅ Incidencia creada:', response.data)
      return response.data
    } catch (error) {
      console.error('❌ Error creando incidencia:', error)
      throw error
    }
  },

  // ✏️ Actualizar incidencia existente
  async updateIncident(id: number, incident: UpdateIncidentRequest): Promise<Incident> {
    try {
      const response = await apiClient.put(`/incidences/${id}`, incident)
      console.log('✅ Incidencia actualizada:', response.data)
      return response.data
    } catch (error) {
      console.error('❌ Error actualizando incidencia:', error)
      throw error
    }
  },

  // 🗑️ Eliminar incidencia
  async deleteIncident(id: number): Promise<void> {
    try {
      await apiClient.delete(`/incidences/${id}`)
      console.log('✅ Incidencia eliminada:', id)
    } catch (error) {
      console.error('❌ Error eliminando incidencia:', error)
      throw error
    }
  },

  // ═══ P13-V1C — operaciones nombradas del ACTIVE HANDLER (claim atómico) ═══
  // El phantom `POST /incidences/:id/assign` fue RETIRADO (nunca existió en el backend; fallaba
  // siempre). La asignación de usuario existe únicamente como la operación canónica assign-user
  // (DH → DH activo del departamento enrutado), y la adquisición propia como claim/release.
  // Ninguna de estas llamadas materializa autoridad: la respuesta trae el estado persistido y los
  // consumidores refetchean tras la señal `incident:handler-changed`.

  // 🤝 Reclamar la incidencia (self-claim; idempotente para el mismo actor)
  async claimIncident(id: number): Promise<ClaimOperationResponse> {
    const response = await apiClient.post(`/incidences/${id}/claim`, {})
    return response.data
  },

  // 🔓 Liberar el claim (handler actual; super_admin sólo como recuperación administrativa)
  async releaseIncident(id: number): Promise<ClaimOperationResponse> {
    const response = await apiClient.post(`/incidences/${id}/release`, {})
    return response.data
  },

  // 👤 Asignar el handler a un usuario elegible del departamento enrutado (DH → DH activo)
  async assignIncidentUser(id: number, targetUserId: number): Promise<ClaimOperationResponse> {
    const response = await apiClient.post(`/incidences/${id}/assign-user`, { targetUserId })
    return response.data
  },

  // 📇 Asignables elegibles (advisory; el backend revalida todo dentro de la transacción)
  async getEligibleAssignees(id: number): Promise<EligibleAssignee[]> {
    const response = await apiClient.get(`/incidences/${id}/eligible-assignees`)
    // Mismo mecanismo que `getClassifications`, falsificado individualmente: el backend emite
    // `{ success, data, count, advisory }` (incidenceClaimController.js:271) y el interceptor por
    // defecto deja `response.data` como la lista. Una lista vacía es legítima (nadie elegible);
    // una forma imposible no puede pasar por "no hay elegibles".
    const assignees = response.data
    if (!Array.isArray(assignees)) {
      throw new Error(`ELIGIBLE_ASSIGNEES_SHAPE_INVALID: expected an array after the default unwrap, received ${assignees === null ? 'null' : typeof assignees}`)
    }
    return assignees
  },

  // 📚 Catálogo de clasificaciones con política efectiva para la administración del actor
  // (opcionalmente contextualizado por pointId para actores globales). Advisory: el intake
  // re-resuelve y falla cerrado si la política desaparece antes del commit.
  async getClassifications(pointId?: number): Promise<IncidentClassificationOption[]> {
    const qs = pointId ? `?pointId=${encodeURIComponent(pointId)}` : ''
    const response = await apiClient.get(`/incidences/classifications${qs}`)
    // UNA sola interpretación del envelope. El interceptor por defecto de `api-client` YA desenvolvió
    // `{ success, data }` (api-client.ts:96-97), así que `response.data` ES el catálogo. Leerlo otra
    // vez con `.data` sobre un array daba `undefined`, y el `?? []` lo convertía en un vacío
    // silencioso: select sin opciones, botón `:disabled` y el intake W4 inalcanzable por UI real.
    const catalog = response.data
    // Un catálogo VACÍO es un estado de negocio legítimo (administración sin política efectiva) y se
    // preserva tal cual. Una forma IMPOSIBLE no puede disfrazarse de "no hay datos": falla nombrada.
    if (!Array.isArray(catalog)) {
      throw new Error(`CLASSIFICATION_CATALOG_SHAPE_INVALID: expected an array after the default unwrap, received ${catalog === null ? 'null' : typeof catalog}`)
    }
    return catalog
  },

  // 🔄 Cambiar estado de incidencia (legacy — usa `status`)
  async updateStatus(id: number, status: string, comment?: string): Promise<void> {
    try {
      const response = await apiClient.put(`/incidences/${id}/status`, {
        status: status.toLowerCase(),
        comment
      })
      console.log('✅ Estado actualizado:', response.data)
    } catch (error) {
      console.error('❌ Error cambiando estado:', error)
      throw error
    }
  },

  // 🔄 Ejecutar acción del state machine (Lote 4.2 — contrato nuevo)
  async executeAction(id: number, action: string, payload: { comment?: string; resolution?: string } = {}): Promise<any> {
    const response = await apiClient.put(`/incidences/${id}/status`, {
      action,
      ...payload
    })
    return response.data
  },

  // 💬 Agregar comentario
  async addComment(id: number, comment: string): Promise<void> {
    try {
      const response = await apiClient.post(`/incidences/${id}/comments`, {
        comment
      })
      console.log('✅ Comentario agregado:', response.data)
    } catch (error) {
      console.error('❌ Error agregando comentario:', error)
      throw error
    }
  },

  // 📎 Subir archivo adjunto (P12 post-Gate-A: delega en el attachment.service
  // canónico — campo multipart 'files', timeout 0 para móviles lentos, sin
  // exponer claves de storage)
  async uploadAttachment(id: number, file: File): Promise<void> {
    const { attachmentService } = await import('@/services/api/attachment.service')
    const { newUploadIdempotencyKey } = await import('@/services/api/uploadIdempotencyKey')
    // P13-V5B O1: cada llamada es UNA acción de subida del usuario ⇒ una clave nueva por llamada.
    await attachmentService.upload({ kind: 'incidence', id }, file, {
      onProgress: () => undefined,
      idempotencyKey: newUploadIdempotencyKey()
    })
  },

  // 👥 Obtener lista de supervisores
  async getSupervisors(): Promise<{ data: Supervisor[] }> {
    try {
      // Intentar obtener usuarios con rol de supervisor desde /users
      const response = await apiClient.get('/users')
      
      // Filtrar usuarios que sean supervisores o usar todos si no hay filtro
      let supervisors = []
      if (response.data && Array.isArray(response.data.data)) {
        supervisors = response.data.data.filter((user: any) => 
          user.role && (user.role.includes('supervisor') || user.role.includes('field'))
        )
      } else if (Array.isArray(response.data)) {
        supervisors = response.data.filter((user: any) => 
          user.role && (user.role.includes('supervisor') || user.role.includes('field'))
        )
      }
      
      // Mapear a formato esperado
      const mappedSupervisors = supervisors.map((user: any) => ({
        id: user.id?.toString() || Math.random().toString(),
        name: user.fullName || user.username || `Usuario ${user.id}`,
        zone: user.department || 'Sin asignar',
        email: user.email || `${user.username}@mgi.do`,
        phone: user.phone || '+1809-555-0000'
      }))
      
      return { data: mappedSupervisors }
    } catch (error) {
      const statusCode = (error as any)?.message?.includes('403') ? 403 : null
      if (statusCode === 403) {
        // Silencioso: devolver lista vacía sin ruido en UI
        return { data: [] }
      }
      // Fallback ejemplo solo si no es 403
      return { data: [] }
    }
  },

  // 🏢 Obtener lista de agencias (Points)
  async getAgencies(): Promise<{ data: Agency[] }> {
    try {
      // P13-PROD-SCALE-BOUNDED-READS-WS1: /points answers one bounded page. The create form's agency
      // picker and the details lookup need the WHOLE catalogue, so every page is read (the envelope is
      // kept for its pagination) or the read fails into the catch below — never a shorter list.
      const points = await fetchAllPointPages(sharedClientPointPages(apiClient, '/points'))
      
      // Mapear points a formato de agencias
      const mappedAgencies = points.map((point: any) => ({
        id: point.id || Math.random().toString(),
        name: point.name || `Agencia ${point.id}`,
        code: point.code || `AG${point.id}`,
        address: point.address || 'Dirección no disponible',
        province: point.province || point.city || 'No especificada',
        zone: point.zone || 'Sin zona',
        phone: point.phone || point.contact_phone || '+1809-555-0000',
        manager: point.manager || 'Sin gerente',
        status: point.is_active || point.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE',
        location: {
          lat: parseFloat(point.latitude) || 18.4861,
          lng: parseFloat(point.longitude) || -69.9312
        }
      }))
      
      return { data: mappedAgencies }
    } catch (error) {
      console.error('Error fetching agencies:', error)
      return { data: [] }
    }
  },

  // P13-V1C: `getStats()` fue RETIRADO — `GET /incidences/stats` nunca existió como ruta (la URL
  // caía en `GET /:id` con id='stats'). Los agregados canónicos viven en /summary y
  // /departments/stats, consumidos por sus propias superficies.
}

// Fase 4 — Lote 4.0/4.2: `generateMockIncidents()` y `generateMockAgencies()` eliminados.
// La política rectora prohíbe data mock, fallbacks simulados y placeholders
// operativos. Cualquier respuesta debe provenir del backend real.
